/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drug shipment delete modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState } from '@ngt/request-utilities';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';

import { useSnackbar } from 'notistack';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { useDrugShipment } from '../../../store/modules/drugmanagement/drugshipment';


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentDeleteModalProps {
    drugShipmentId: number;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onFormClose?: () => void;
    onFormSave?: () => void;
}

const DrugShipmentDeleteModal: React.FunctionComponent<IDrugShipmentDeleteModalProps> = ({
    drugShipmentId,
    modalOpen,
    setModalOpen,
    onFormClose,
    onFormSave
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const [, , , , drugShipmentDeleteState, drugShipmentActions] = useDrugShipment();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        drugShipmentActions.deleteById(drugShipmentId);
    }, [drugShipmentId, drugShipmentActions]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);

        if (onFormClose) {
            onFormClose();
        }
    }, [setModalOpen, onFormClose]);

    React.useEffect(() => {
        if (drugShipmentDeleteState && drugShipmentDeleteState.state && drugShipmentDeleteState.state === RequestState.Success) {
            setModalOpen(false);

            if (onFormSave) {
                onFormSave();
            }
        }
        else if (drugShipmentDeleteState && drugShipmentDeleteState.state && drugShipmentDeleteState.state === RequestState.Failure) {
            let errorMessage = 'Unknown error';

            if (drugShipmentDeleteState.responseStatus?.message) {
                errorMessage = drugShipmentDeleteState.responseStatus?.message;
            }

            enqueueSnackbar(`An error occurred while attempting to delete the drug shipment: ${errorMessage}`, { variant: 'error' });
        }
    }, [drugShipmentDeleteState, setModalOpen, onFormSave, enqueueSnackbar]);

    const formDisabled = React.useMemo(() => {
        return drugShipmentDeleteState && drugShipmentDeleteState.state && drugShipmentDeleteState.state === RequestState.Pending;
    }, [drugShipmentDeleteState]);

    const dialogText = `This will delete the drug shipment and add the drugs back to the batch at the depot. Do you wish to continue?`;

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Delete Drug Shipment
                </DialogTitle>
                <DialogContent>
                    {dialogText}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                        Cancel
                    </Button>
                    <Button onClick={onOkClick}
                        color="primary" autoFocus disabled={formDisabled}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default DrugShipmentDeleteModal;